import React, { useEffect, useState } from "react";
import {
  fetchAuthSession,
  getCurrentUser,
  fetchUserAttributes,
  updateUserAttributes,
  updatePassword,
} from "aws-amplify/auth";
import AuthInterceptor from "../../interceptors/authInterceptor.jsx";
import Layout from "../../components/layout/UserLayout.jsx";
import Modal from "react-modal";
import { AvatarCustomizer } from "../../components/shared"; // Asegúrate de importar correctamente
import { useAuth } from "../../config/AuthProvider.js";
import { ClockLoader } from "react-spinners";

// Configura el elemento raíz para accesibilidad
Modal.setAppElement("#root");

function Configuracion() {
  const { checkAuth } = useAuth();
  const [payloadData, setPayloadData] = useState(null);
  const [editableAttribute, setEditableAttribute] = useState({
    name: "",
    value: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchPayloadData();
  }, []);

  // Función para obtener los atributos del usuario autenticado
  const fetchPayloadData = async () => {
    try {
      // Obtenemos la sesión actual
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken;
      const payload = idToken.payload;

      // Obtenemos el usuario actual
      const user = await getCurrentUser();

      // Obtenemos los atributos del usuario
      const attributes = await fetchUserAttributes(user);

      // attributes es un objeto, lo combinamos directamente
      const combinedData = { ...payload, ...attributes };

      // Ahora setPayloadData con los datos combinados
      setPayloadData(combinedData);
    } catch (error) {
      console.error("Error al obtener los atributos del usuario:", error);
    }
  };

  // Función para abrir el modal y cargar el dato editable
  const openModal = (attributeName, currentValue) => {
    if (attributeName === "password") {
      setEditableAttribute({
        name: "password",
        currentPassword: "",
        newPassword: "",
      });
    } else {
      setEditableAttribute({ name: attributeName, value: currentValue });
    }
    setIsModalOpen(true);
  };

  // Función para cerrar el modal y resetear los estados
  const closeModal = () => {
    setIsModalOpen(false);
    setEditableAttribute({ name: "", value: "" });
  };

  // Maneja los cambios en el input del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditableAttribute({
      ...editableAttribute,
      [name]: value,
    });
  };

  // Maneja el envío del formulario para actualizar el dato
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editableAttribute.name === "password") {
        // Manejar cambio de contraseña
        if (
          editableAttribute.currentPassword &&
          editableAttribute.newPassword
        ) {
          await updatePassword({
            oldPassword: editableAttribute.currentPassword,
            newPassword: editableAttribute.newPassword,
          });
          closeModal();
        } else {
          alert(
            "Por favor, ingrese la contraseña actual y la nueva contraseña.",
          );
          return;
        }
      } else {
        // Actualizar el atributo
        const userAttributes = {
          userAttributes: {
            [editableAttribute.name]: editableAttribute.value,
          },
        };
        userAttributes[editableAttribute.name] = editableAttribute.value;
        await updateUserAttributes(userAttributes);

        // Refrescamos los datos del usuario
        await fetchPayloadData();
        // await checkAuth();
        closeModal();
      }
    } catch (error) {
      console.error("Error al actualizar el dato del usuario:", error);
      alert(
        "Hubo un error al actualizar el dato. Por favor, inténtalo de nuevo.",
      );
    }
  };

  // Función para manejar el guardado del avatar desde AvatarCustomizer
  const handleAvatarSave = async (avatarDataUrl) => {
    try {
      if (!avatarDataUrl || typeof avatarDataUrl !== "string") {
        console.error("URL del avatar no válida:", avatarDataUrl);
        alert("Hubo un error con la URL del avatar. Inténtalo de nuevo.");
        return;
      }

      await updateUserAttributes({
        userAttributes: {
          picture: avatarDataUrl,
        },
      });

      await fetchPayloadData();
      await checkAuth();
      closeModal();
    } catch (error) {
      console.error("Error al actualizar el avatar del usuario:", error);
    }
  };

  if (!payloadData) {
    return (
      <Layout>
        <AuthInterceptor>
          <div className="flex h-screen w-screen items-center justify-center bg-gray-100">
            <ClockLoader color="#00f" size={100} />
          </div>
        </AuthInterceptor>
      </Layout>
    );
  }

  const getAttributeDisplayName = (attributeName) => {
    const displayNames = {
      name: "Nombre",
      family_name: "Apellido",
      birthdate: "Fecha de Nacimiento",
      gender: "Género",
      phone_number: "Número de Teléfono",
      picture: "Foto de Perfil",
      email: "Email",
      // Añade otros atributos si es necesario
    };
    return displayNames[attributeName] || attributeName;
  };

  const getInputType = (attributeName) => {
    switch (attributeName) {
      case "birthdate":
        return "date";
      case "phone_number":
        return "tel";
      case "picture":
        return "text";
      case "gender":
        return "text"; // o 'select' si deseas opciones
      default:
        return "text";
    }
  };

  // Definimos el orden de los atributos
  const orderedAttributes = [
    "name",
    "family_name",
    "gender",
    "phone_number",
    "email",
    "birthdate",
    "picture",
    "password",
  ];

  return (
    <Layout>
      <AuthInterceptor>
        <div className="p-6">
          <h1 className="mb-4 text-2xl font-bold">Perfil de Usuario</h1>
          <table className="min-w-full bg-white">
            <tbody>
              {orderedAttributes.map((key) => {
                const value = payloadData[key];

                // Si el valor no existe, no renderizamos la fila
                if (value === undefined) return null;

                return (
                  <tr key={key}>
                    <td className="border-b border-gray-200 px-4 py-2">
                      <strong>{getAttributeDisplayName(key)}</strong>
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2">
                      {key === "picture" ? (
                        <img
                          src={value}
                          alt="Foto de Perfil"
                          className="h-16 w-16 rounded-full"
                        />
                      ) : (
                        value
                      )}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2">
                      {/* Si la clave es email, no mostramos el botón de Editar */}
                      {key !== "email" && (
                        <button
                          onClick={() => openModal(key, value)}
                          className="rounded bg-blue-500 px-4 py-2 text-white"
                        >
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {/* Agregar manualmente el botón para cambiar la contraseña */}
              <tr>
                <td className="border-b border-gray-200 px-4 py-2">
                  <strong>Contraseña</strong>
                </td>
                <td className="border-b border-gray-200 px-4 py-2">••••••••</td>
                <td className="border-b border-gray-200 px-4 py-2">
                  <button
                    onClick={() => openModal("password")}
                    className="rounded bg-blue-500 px-4 py-2 text-white"
                  >
                    Editar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Modal para editar el dato */}
          <Modal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            contentLabel="Editar Dato"
            className="relative mx-auto my-8 w-full max-w-2xl overflow-auto rounded bg-white p-6 shadow-lg"
            overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          >
            {editableAttribute.name === "picture" ? (
              <AvatarCustomizer
                initialOptions={{
                  // Pasa las opciones iniciales si están almacenadas
                  seed: payloadData["custom:avatarSeed"] || "",
                  flip: payloadData["custom:avatarFlip"] || false,
                  // backgroundColor: payloadData['custom:avatarBackgroundColor'] || 'transparent',
                  eyebrows: payloadData["custom:avatarEyebrows"] || "",
                  eyes: payloadData["custom:avatarEyes"] || "",
                  // glasses: payloadData['custom:avatarGlasses'] || 'none',
                  mouth: payloadData["custom:avatarMouth"] || "",
                }}
                onSave={handleAvatarSave}
                onCancel={closeModal}
              />
            ) : (
              <>
                <h2 className="mb-4 text-xl font-bold">
                  Editar {getAttributeDisplayName(editableAttribute.name)}
                </h2>
                <form onSubmit={handleSubmit}>
                  {editableAttribute.name === "password" ? (
                    <>
                      <div className="mb-4">
                        <label className="block text-gray-700">
                          Contraseña Actual:
                        </label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={editableAttribute.currentPassword || ""}
                          onChange={handleChange}
                          className="w-full rounded border px-3 py-2"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-gray-700">
                          Nueva Contraseña:
                        </label>
                        <input
                          type="password"
                          name="newPassword"
                          value={editableAttribute.newPassword || ""}
                          onChange={handleChange}
                          className="w-full rounded border px-3 py-2"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="mb-4">
                      <label className="block text-gray-700">
                        {getAttributeDisplayName(editableAttribute.name)}:
                      </label>
                      {editableAttribute.name === "gender" ? (
                        <select
                          name="value"
                          value={editableAttribute.value}
                          onChange={handleChange}
                          className="w-full rounded border px-3 py-2"
                        >
                          <option value="">Seleccione</option>
                          <option value="Masculino">Masculino</option>
                          <option value="Femenino">Femenino</option>
                          <option value="Otro">Otro</option>
                        </select>
                      ) : (
                        <input
                          type={getInputType(editableAttribute.name)}
                          name="value"
                          value={editableAttribute.value}
                          onChange={handleChange}
                          className="w-full rounded border px-3 py-2"
                        />
                      )}
                    </div>
                  )}
                  {/* Botones de acción */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="mr-4 rounded bg-gray-500 px-4 py-2 text-white"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="rounded bg-green-500 px-4 py-2 text-white"
                    >
                      Guardar Cambios
                    </button>
                  </div>
                </form>
              </>
            )}
          </Modal>
        </div>
      </AuthInterceptor>
    </Layout>
  );
}

export default Configuracion;
